import {
  VNode,
  computed,
  h,
  defineComponent,
  ref,
  watch,
  onBeforeUnmount,
  onMounted,
} from 'vue';
// @ts-ignore
import { BNavbar, BNavbarItem } from 'buefy/dist/esm/navbar';
import { navbarItem } from '@/ui/components/navigation/NavbarItem';
import {
  analyticsPermissions,
  approvalsEnabledForOrganization,
  approvalsPermissions,
  canOpenSubscriptionPage,
  canUpgrade,
  fetchUser,
  findQuota,
  hasPermission,
  hasSomePermission,
  isOnFreePlan,
  isOrganizationAdministrator,
  isOrganizationCreatedAfterAnalyticsV2,
  isTrainingsModuleEnabled,
  Permissions,
  showBuyNowButton,
  user,
} from '@/ui/auth/auth';
import { getLocale, n, t } from '@/ui/i18n/i18n';
import { LoggedInUser, UserQuota } from '@/entities/auth/loggedInUser';
import { Icon } from '../icon/Icon';
import { Tag, tag } from '../Tag';
import { NavbarHomeItem } from '@/ui/components/navigation/NavbarHomeItem';
import { eventBus } from '@/_legacy/eventBus';
import { Events } from '@/ui/events';
import { QuotaInfo } from '@/ui/components/navbar/QuotaInfo';
import { unixTimestampToDate, whenUserIsOnFreeTrialPlan } from '@/ui/helpers';
import { starredItemCurried } from '@/ui/components/upgrade/StarredItem';
import { flow } from 'fp-ts/function';
import { ddr, dd } from '@/ui/components/navigation/NavbarDropdown';
import { upgradeOrSubscribeButton } from '@/ui/components/upgrade/UpgradeOrSubscribeButton';
import { differenceInDays } from 'date-fns';
import { FeatureNames, newFeature, hasNewFeature } from '@/ui/components/NewFeature';
import { COUNTRY_CODE_GERMANY } from '@/_legacy/constants';
import IntegrationsNavbarButton from '@/ui/components/integrations/NavbarButton.vue';
import { areApprovalsEnabled, isAnalyticsEnabled } from '@/ui/helpers/featureHelper';
import AiCreditsBanner from '@/ui/components/navbar/AiCreditsBanner.vue';

const templateTagName = 'template';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const navbarLeftSlotName = 'start';
const navbarRightSlotName = 'end';

interface NavbarProps {
  fixedTop: boolean;
  classes: string;
}

export const Navbar = defineComponent<NavbarProps>({
  name: 'Navbar',
  props: ['fixedTop', 'classes'],
  setup(props) {
    const unreadBadge = (loggedInUser: LoggedInUser) => (
      loggedInUser.unreadNotifications > 0
        ? tag('error', n(loggedInUser.unreadNotifications, 'integer'))
        : null
    );

    const referralProgramLink = getLocale() === COUNTRY_CODE_GERMANY.toLowerCase()
      ? 'https://share.hsforms.com/1i4d7FwelT9yCLfw9MF_XaQ2z09m'
      : 'https://share.hsforms.com/1mBzY_G2URCmt-KSVSkuqqw2z09m';

    const initialQuota = findQuota('inspection');
    const quota = ref<UserQuota | undefined>(initialQuota);
    const updateQuota = () => {
      quota.value = findQuota('inspection');
    };

    async function onInspectionDeleted({ isNew }: { isNew: boolean }) {
      //for quick change display
      if (quota.value && isNew) {
        quota.value.current -= 1;
      }
      //actually load new quota
      await fetchUser()();
      updateQuota();
    }

    onMounted(async () => {
      await fetchUser()();
      updateQuota();

      eventBus.$on(Events.inspectionDeleted, onInspectionDeleted);
    });

    onBeforeUnmount(() => {
      eventBus.$off(Events.inspectionDeleted, onInspectionDeleted);
    });

    const starred = flow(
      navbarItem,
      (node: VNode) => [node],
      starredItemCurried(isOnFreePlan.value),
    );

    const renderAnalytics = (label: string) => {
      if (!isAnalyticsEnabled() || isOrganizationCreatedAfterAnalyticsV2.value) {
        return starred(label, 'analytics-v2');
      }

      return dd(label, [
        navbarItem(
          [label, h(Tag, { class: 'tag--left-margin', props: { type: 'purple' } }, t('new-features.legacy'))],
          'analytics',
        ),
        navbarItem(
          [label, h(Tag, { class: 'tag--left-margin', props: { type: 'success' } }, t('new-features.new'))],
          'analytics-v2',
        ),
      ], 'navbar-item--analytics');
    };

    const showHardwareAndSensors = computed(
      // show sensors menu if has hardware
      () => hasPermission(Permissions.MANAGE_HARDWARE) && user.value._legacy_has_hardware,
    );

    const trialDaysLeft = computed(() => {
      if (user.value._legacy_subscription?.trial_ends_at) {
        return differenceInDays(unixTimestampToDate(user.value._legacy_subscription?.trial_ends_at), new Date());
      }
      if (user.value.subscription?.trialEnd) {
        return differenceInDays(unixTimestampToDate(user.value.subscription?.trialEnd), new Date());
      }
      return 0;
    });

    watch(user, updateQuota);

    const showApprovals = () => (
      approvalsEnabledForOrganization.value
      && hasSomePermission(approvalsPermissions)
    ) || ( //promote the addon to the admin
      areApprovalsEnabled()
      && isOrganizationAdministrator()
    );

    const showAdministration = () => hasPermission(Permissions.MANAGE_USERS)
      || hasPermission(Permissions.MANAGE_PERMISSIONS) || hasPermission(Permissions.MANAGE_CATEGORIES);

    const showEntities = () => hasSomePermission([
      Permissions.MANAGE_ENTITY_ITEMS,
      Permissions.MANAGE_ENTITY_FOLDERS,
      Permissions.MANAGE_ENTITY_TYPES,
      Permissions.MANAGE_ENTITY_PROPERTIES,
    ]);

    const showAnalytics = () => hasSomePermission(analyticsPermissions);

    return function render(): VNode {
      const divider = () => h('hr', { class: 'navbar-divider' });

      return h('div', [
        h(BNavbar,
          {
            props: {
              'close-on-click': true,
              transparent: true,
              type: 'is-transparent',
            },
            class: props.classes,
          }, [
            h(templateTagName, { slot: navbarLeftSlotName }, [
              h(NavbarHomeItem),
              navbarItem(t('common.checklists'), 'forms'),
              //show this menu entry always
              navbarItem(t('common.issues'), 'issueIndex'),
              showApprovals() ? navbarItem([
                t('common.approvals'),
                h(Tag, { class: 'tag--left-margin tag--no-right-margin', props: { type: 'success' } }, t('new-features.new')),
              ], 'approvalIndex') : null,
              isTrainingsModuleEnabled.value ? navbarItem(t('common.training'), 'courses') : null,
              showHardwareAndSensors.value ? navbarItem(t('common.sensors'), 'sensors') : null,
              showAdministration() ? navbarItem(t('navbar.administration'), 'administration') : null,
              showEntities() ? navbarItem(t('navbar.entities'), 'entityItems') : null,
              showAnalytics() ? renderAnalytics(t('common.analytics')) : null,
            ]),
            h(templateTagName, { slot: navbarRightSlotName }, [
              whenUserIsOnFreeTrialPlan(user.value, h('span', {
                class: `navbar-item navbar-item--days-left-trial navbar-item--days-left-trial--${getLocale()}`,
              }, [
                h('div', { class: 'mr-2' }, t('common.days-left-trial', { number: trialDaysLeft.value.toString() })),
              ])),
              quota.value
                ? h(QuotaInfo, {
                  props: {
                    userQuota: quota.value,
                  },
                }) : null,
              showBuyNowButton.value
                ? upgradeOrSubscribeButton()
                : null,
              canUpgrade() ? upgradeOrSubscribeButton({}, { target: 'navbar' }) : null,
              h(IntegrationsNavbarButton),
              ddr([h('div', { class: 'flex items-center' }, [
                h(Icon, { props: { icon: 'user-circle', classes: 'user-circle-icon' } }),
                unreadBadge(user.value),
              ])], [
                h('div', {
                  class: 'navbar-item',
                  style: 'display: block;',
                }, [
                  h('h3', { class: 'title is-6' }, user.value.name),
                  h('h4', { class: 'subtitle is-7' }, user.value.organization.title),
                ]),
                divider(),
                navbarItem([
                  h('span', { style: 'margin-right: 0.5rem;' }, t('common.notifications')),
                  unreadBadge(user.value),
                ], 'notificationIndex'),
                canOpenSubscriptionPage() ? navbarItem(t('common.subscriptions'), 'subscription') : null,
                h(
                  BNavbarItem,
                  {
                    props: {
                      tag: 'a',
                    },
                    attrs: {
                      href: referralProgramLink,
                      target: '_blank',
                    },
                  },
                  [
                    h('div', { class: 'flex' }, [
                      t('common.referral-program'),
                      hasNewFeature(FeatureNames.REFERRAL_PROGRAM)
                        ? newFeature({ featureName: FeatureNames.REFERRAL_PROGRAM, classes: 'ml-1' })
                        : null,
                    ]),
                  ],
                ),
                navbarItem(t('common.profile'), 'profile'),
                divider(),
                navbarItem([
                  h(Icon, {
                    props: {
                      icon: 'sign-out-alt',
                      classes: 'mr-2',
                    },
                  }),
                  t('common.logout'),
                ], 'logout'),
              ]),
            ]),
          ]),
        h(AiCreditsBanner),
      ]);
    };
  },
});
